// src/features/client_portfolios/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

const FormSchema = z.object({
  strategy_from_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  strategy_from_id: z.coerce.number().optional().nullable(),

  strategy_to_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  strategy_to_id: z.coerce.number().optional().nullable(),

  closing_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),

  weight: z.coerce.number().optional().nullable(),
  market_value: z.coerce.number().optional().nullable(),
});

export function ClientPortfoliosEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "strategy_from_name") {
        return {
          text: fetchedData.strategy_from_name,
          id: fetchedData.strategy_from_id,
          name: fetchedData.strategy_from_name,
        };
      }
      if (key === "strategy_to_name") {
        return {
          text: fetchedData.strategy_to_name,
          id: fetchedData.strategy_to_id,
          name: fetchedData.strategy_to_name,
        };
      }
      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                ClientPortfolios {id}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Strategy From & Strategy To */}
                <AutocompleteAsyncField
                  name="strategy_from_name"
                  label={t(`${table_name}.columns.strategy_from_name`, {
                    ns: "tables",
                    defaultValue: "Strategy From",
                  })}
                  base_url="etl-quantilia/strategies"
                  column_name="name"
                  id_column="strategy_from_id"
                  control={form.control}
                />
                <AutocompleteAsyncField
                  name="strategy_to_name"
                  label={t(`${table_name}.columns.name`, {
                    ns: "tables",
                    defaultValue: "Strategy To",
                  })}
                  base_url="etl-quantilia/strategies"
                  column_name="name"
                  id_column="strategy_to_id"
                  control={form.control}
                />

                {/* Row 2: Closing Date & Weight */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="closing_date"
                    label={t(`${table_name}.columns.closing_date`, {
                      ns: "tables",
                      defaultValue: "Closing Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="weight"
                    label={t(`${table_name}.columns.weight`, {
                      ns: "tables",
                      defaultValue: "Weight",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>

                {/* Row 3: Market Value */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="market_value"
                    label={t(`${table_name}.columns.market_value`, {
                      ns: "tables",
                      defaultValue: "Market Value",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["strategy_from_id", "strategy_to_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
